import React, { useEffect, useRef } from 'react';
import getConfig from 'next/config';
import { useRouter } from 'next/compat/router';
import { MediaQueryProvider } from 'react-media-query-hoc';
import { Breadcrumbs } from '@domain-group/fe-co-header';
import { Paginator } from '@domain-group/fe-blueprint-ui/pagination';
import { srOnly } from '@domain-group/fe-brary';
import { InfoIcon } from '@domain-group/fe-co-icon';
import { Tooltip as ToolTip } from '@domain-group/fe-blueprint-ui/tooltip';
import {
  SALES_AGENT,
  AGENCY_VIEW,
  AGENT_VIEW,
  DEFAULT_SORT_LABEL,
  DEFAULT_SORT_VALUE,
  SORT_OPTIONS_AGENCIES,
  SORT_OPTIONS_AGENTS,
} from './components/search-filter-bar/enums';
import {
  AppraisalRequestCard,
  SearchResultsHead,
  SellingGuideCard,
  TwoColumnLayout,
} from '..';
import SearchResultsFooterWithFragment from '../footer/search-results-footer-with-fragment';
import { mapSearchResultsBreadcrumbs } from '../header/utils';
import {
  useRouteChangeLoading,
  useSearchResults,
  useSearchState,
} from './hooks';
import { Option } from './components/search-filter-bar/filters-bar/pill-forms/filters-form';
import {
  SearchFilterBar,
  SearchSwitchViewButtons,
  SortBy,
  Loader,
  LoadingOverlay,
  Results,
} from './components';
import { buildUrl, buildPaginatorUrl } from './utils';
import {
  formatSuburbsForTracking,
  getFirstSuburbName,
  mapAgentFilterToEnum,
} from './utils/format-suburbs-for-tracking';
import { notEmpty } from '../../utils';
import { UserDetailsRedacted } from '../../@types';
import {
  trackViewSearchResultsPage,
  trackFilterResults,
  trackSelectSortOrder,
  trackViewTooltip,
} from '../../tracking/search-results';
import { SearchTypeView } from '../../tracking/search-results/enums';

import * as styles from './search-results.style';

const { publicRuntimeConfig } = getConfig();

interface SearchResultsProps {
  mixpanelHasInitialized?: boolean;
  isEmbeddedApp?: boolean;
  skipLinkContentId: string;
  user?: UserDetailsRedacted;
  // Initial (SSR) search
  sortBy?: string;
  keywordName?: string;
  agentFilter?: string;
  page: string;
  isAgency: boolean;
  suburbs: string;
}

const SearchResults = ({
  mixpanelHasInitialized = false,
  isEmbeddedApp = false,
  skipLinkContentId,
  user,
  // Initial (SSR) search
  page: initialPage,
  keywordName: initialKeyword,
  agentFilter: initialAgentFilter,
  sortBy: initialSortBy,
  isAgency: initialIsAgency,
  suburbs: initialSuburbs,
}: SearchResultsProps): JSX.Element => {
  const router = useRouter();
  const routeLoading = useRouteChangeLoading(router);

  const state = useSearchState({
    isAgency: initialIsAgency,
    suburbs: initialSuburbs,
    page: initialPage,
    keywordName: initialKeyword,
    agentFilter: initialAgentFilter,
    sortBy: initialSortBy,
  });

  // Enforce canonical URLs by checking if the current URL matches a canonical URL
  // useEffect(() => {
  //   if (state && router.asPath) {
  //     const { to: toPath, as: asPath } = buildUrl(state);
  //     if (router.asPath !== asPath) {
  //       router.replace(toPath, asPath, { shallow: true });
  //     }
  //   }
  // }, [state, router]);

  const { data, loading, error, actualLocationProfiles, search, agencyIds } =
    useSearchResults(state);

  const trackedSearchResultsPathRef = useRef<string | null>(null);
  const isInitialRenderRef = useRef(true);
  // Hold latest data/state in refs so the tracking effect can read current values
  // without them being listed as dependencies (both are new object references every render).
  const dataRef = useRef(data);
  dataRef.current = data;
  const stateRef = useRef(state);
  stateRef.current = state;
  const locationProfilesKey =
    data?.locationProfiles
      ?.map((profile) => profile?.urlSlug)
      .filter(notEmpty)
      .join('|') ?? '';
  const stateSortBy = state?.sortBy;
  const stateAgentFilter = state?.agentFilter;
  const stateIsAgency = state?.isAgency;

  // Record page view with search details
  useEffect(() => {
    const currentPath = router?.asPath;
    const currentData = dataRef.current;
    const currentState = stateRef.current;

    if (
      !currentPath ||
      !mixpanelHasInitialized ||
      !currentData?.locationProfiles ||
      !currentState
    ) {
      return;
    }

    // Prevent duplicate page-view events caused by object/array reference changes.
    if (trackedSearchResultsPathRef.current === currentPath) {
      return;
    }

    // Format suburb data for tracking
    const suburbsForTracking = formatSuburbsForTracking(
      currentData.locationProfiles,
    );
    const firstSuburb = getFirstSuburbName(currentData.locationProfiles);

    // Get results count based on search type
    const resultsCount = currentState.isAgency
      ? currentData?.agencySearchBySuburbs?.total
      : currentData?.contactSearchBySuburbs?.total;

    const SORT_OPTIONS_FOR_VIEW = currentState.isAgency
      ? SORT_OPTIONS_AGENCIES
      : SORT_OPTIONS_AGENTS;
    const sortByLabel =
      SORT_OPTIONS_FOR_VIEW.find(
        (option) => option.value === currentState.sortBy,
      )?.label ?? DEFAULT_SORT_LABEL;

    trackViewSearchResultsPage({
      suburb: firstSuburb,
      searchTypeView: SearchTypeView.ListView,
      searchSortBy: sortByLabel,
      searchFilterSuburb: suburbsForTracking,
      searchFilterAgentType: currentState.isAgency
        ? undefined
        : mapAgentFilterToEnum(currentState.agentFilter),
      searchResultsReturnedCount: resultsCount ?? undefined,
    });

    trackedSearchResultsPathRef.current = currentPath;
    isInitialRenderRef.current = false;
  }, [
    router?.asPath,
    mixpanelHasInitialized,
    locationProfilesKey,
    stateSortBy,
    stateAgentFilter,
    stateIsAgency,
    data?.agencySearchBySuburbs?.total,
    data?.contactSearchBySuburbs?.total,
  ]);

  if (
    typeof window !== 'undefined' &&
    !routeLoading &&
    !loading &&
    (!state || !actualLocationProfiles.length)
  ) {
    // If no suburbs, redirect to landing page
    // Needs to happen client-side (can't redirect here during getServerSideProps)
    router?.push?.('/real-estate-agents');
  }

  if (error) {
    throw new Error(`Something went wrong, ${error}`);
  }

  if (!data || !state || !actualLocationProfiles.length) {
    return <Loader />;
  }

  const { isAgency, suburbs, page, keywordName, agentFilter, sortBy } = state;

  const searchView = isAgency ? AGENCY_VIEW : AGENT_VIEW;

  const breadcrumbs = mapSearchResultsBreadcrumbs({
    isAgent: !isAgency,
    suburbs: data?.locationProfiles,
  });

  const changePage = (filters: {
    suburbs?: string[];
    page?: number;
    keywordName?: string;
    agentFilter?: string;
    sortBy?: string; // TODO: Use enum
    isAgency?: boolean;
  }): void => {
    // Avoid doing client-side routing if the suburbs are changing
    // This is a somewhat hacky fix - we should look at using virtual page view events instead
    const shouldBeShallow = typeof suburbs === 'undefined';

    const newState = {
      ...state,
      ...filters,
    };

    const { to: toPath, as: asPath } = buildUrl(newState);

    router?.push(toPath, asPath, {
      // https://nextjs.org/docs/routing/shallow-routing
      shallow: shouldBeShallow,
    });
  };

  const handlePageChange = (event: React.SyntheticEvent, newPage: number) => {
    event.preventDefault();

    window?.scrollTo({
      top: 0,
      behavior: 'smooth',
    });

    changePage({
      page: newPage,
    });
  };

  // Change filters/state
  const selectSuburbs = (newSuburbs: Option[] | null) => {
    changePage({
      suburbs: newSuburbs?.map?.(({ value }) => value) ?? [],
    });
  };

  const selectFilter = (newFilter: string | null) => {
    const formattedNewFilter = newFilter ?? '';
    trackFilterResults(formattedNewFilter);
    changePage({
      keywordName: formattedNewFilter,
    });
  };

  const setSortBy = (newSortBy: string) => {
    const SORT_OPTIONS = isAgency ? SORT_OPTIONS_AGENCIES : SORT_OPTIONS_AGENTS;
    const sortOption = SORT_OPTIONS.find((item) => item.value === newSortBy);

    trackSelectSortOrder(sortOption?.label ?? DEFAULT_SORT_LABEL);
    changePage({
      sortBy: sortOption?.value ?? DEFAULT_SORT_VALUE,
    });
  };

  const selectAgentType = (newAgentType: string | null) => {
    changePage({
      agentFilter: newAgentType ?? SALES_AGENT,
    });
  };

  const selectSearchTypeAndFilters = (
    newSearchType: string | null,
    newAgentType: string | null,
    newFilter: string | null,
  ) => {
    const formattedOldFilter = state.keywordName ?? '';
    const formattedNewFilter = newFilter ?? '';
    if (newFilter !== formattedOldFilter) {
      trackFilterResults(formattedNewFilter);
    }

    const isAgencyNew = newSearchType === AGENCY_VIEW;
    changePage({
      isAgency: isAgencyNew,
      // Reset other filters
      page: 1,
      ...(isAgencyNew ? {} : { agentFilter: newAgentType ?? SALES_AGENT }),
      keywordName: formattedNewFilter,
      sortBy: DEFAULT_SORT_VALUE,
    });
  };

  const selectSearchType = (newSearchType: string | null) => {
    changePage({
      isAgency: newSearchType === AGENCY_VIEW,
      // Reset other filters
      page: 1,
      agentFilter: SALES_AGENT,
      keywordName: '',
      sortBy: DEFAULT_SORT_VALUE,
    });
  };

  const isLoading = !!(loading || routeLoading);

  const urlTemplate = buildPaginatorUrl({
    isAgency,
    suburbs,
    sortBy,
    agentFilter,
    keywordName,
    page,
  });

  return (
    <MediaQueryProvider values={{ width: 300, type: 'screen' }}>
      <div id={skipLinkContentId} data-testid="trade-search-results__page">
        <SearchResultsHead
          isAgency={isAgency}
          total={search?.total}
          totalPages={search?.totalPages}
          pageNumber={page}
          suburbOptions={actualLocationProfiles}
          suburbSlugsArray={suburbs}
          sortBy={sortBy}
          agentType={agentFilter}
          keyword={keywordName}
          breadcrumbs={breadcrumbs}
          agencyIds={agencyIds}
          user={user}
          agentResults={data?.contactSearchBySuburbs?.results}
          agencyResults={data?.agencySearchBySuburbs?.results}
        />
        <SearchFilterBar
          isAgency={isAgency}
          suburbOptions={actualLocationProfiles}
          selectSuburbs={selectSuburbs}
          selectSearchType={selectSearchType}
          currentSearchType={searchView}
          selectAgentType={selectAgentType}
          currentAgentType={agentFilter}
          selectFilter={selectFilter}
          currentFilter={keywordName}
          selectSearchTypeAndFilters={selectSearchTypeAndFilters}
        />
        {!isEmbeddedApp && (
          <div css={styles.breadcrumbWrapper}>
            <Breadcrumbs
              baseUrl={publicRuntimeConfig.BASE_URL}
              breadcrumbs={breadcrumbs}
              backButtonLabel="Back"
              gutter
            />
          </div>
        )}
        <main>
          <TwoColumnLayout marginBottom="l">
            <div css={styles.titleRow}>
              <h1 css={styles.title} aria-live="polite">
                <span css={srOnly}>Found </span>
                {isAgency
                  ? data?.agencySearchBySuburbs?.total
                  : data?.contactSearchBySuburbs?.total}{' '}
                real estate {isAgency ? 'agencies' : 'agents'} active in{' '}
                {actualLocationProfiles[0]?.suburbName ||
                  `${actualLocationProfiles.length ?? 0} suburbs`}
              </h1>
              <ToolTip
                mouseEnterDelay={0.2}
                content="Listing counts are for properties advertised on Domain within searched location."
                layer="layerFloating"
                placement="bottom"
                trigger="hover"
                css={styles.tooltip}
              >
                <InfoIcon size="small" />
              </ToolTip>
            </div>
            <p css={styles.subTitle}>
              Domain does not make any representation about the accuracy of the
              reviews which are provided by RateMyAgent.
            </p>
            <div css={styles.sortGroup}>
              <SearchSwitchViewButtons
                isAgency={isAgency}
                onClick={selectSearchType}
                agencyCount={data?.agencySearchBySuburbs?.total}
                contactCount={data?.contactSearchBySuburbs?.total}
              />
              <SortBy
                isAgency={isAgency}
                setSortBy={setSortBy}
                selectedSortBy={sortBy}
                viewSortByTooltip={trackViewTooltip}
              />
            </div>
          </TwoColumnLayout>
          <TwoColumnLayout
            aside={
              <>
                <AppraisalRequestCard />
                <SellingGuideCard />
              </>
            }
            marginBottom="l"
          >
            <LoadingOverlay
              isLoading={isLoading}
              contentCss={styles.resultsGroup}
            >
              <Results
                isAgency={isAgency}
                agentType={agentFilter}
                agentResults={data?.contactSearchBySuburbs?.results}
                agencyResults={data?.agencySearchBySuburbs?.results}
                userId={user?.userId}
                userToken={user?.userToken}
                suburbSlugs={actualLocationProfiles
                  .map((location) => location?.urlSlug)
                  .filter(notEmpty)}
              />
            </LoadingOverlay>
            <div css={styles.pagination} data-testid="search-paginator">
              <Paginator
                totalPages={
                  (isAgency
                    ? data?.agencySearchBySuburbs?.totalPages
                    : data?.contactSearchBySuburbs?.totalPages) ?? 0
                }
                currentPage={page}
                urlTemplate={urlTemplate}
                onPageChange={handlePageChange}
              />
            </div>
          </TwoColumnLayout>
        </main>
        {isEmbeddedApp ? null : (
          <SearchResultsFooterWithFragment
            id={
              actualLocationProfiles.length === 1
                ? actualLocationProfiles[0]?.id
                : null
            }
          />
        )}
      </div>
    </MediaQueryProvider>
  );
};

export default SearchResults;
